import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Extension } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { SuggestionOptions } from '@tiptap/suggestion';
import tippy from 'tippy.js';
import { 
  Type, 
  Heading1, 
  Heading2, 
  Heading3, 
  List, 
  ListOrdered, 
  Quote, 
  Code, 
  Image, 
  Video, 
  Link, 
  Minus
} from 'lucide-react';

export interface SlashCommand {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  command: (props: { editor: any; range: any }) => void;
  aliases?: string[];
}

export interface SlashCommandsOptions {
  commands?: SlashCommand[];
  onImageUpload?: (callback: (imageUrl: string) => void) => void;
  onVideoUpload?: (callback: (videoUrl: string) => void) => void;
}

interface SlashCommandsListProps {
  items: SlashCommand[];
  command: (item: SlashCommand) => void;
}

interface SlashCommandsListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const SlashCommandsList = forwardRef<SlashCommandsListRef, SlashCommandsListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) {
        command(item);
      }
    };

    const upHandler = () => {
      setSelectedIndex((prevIndex) => 
        prevIndex === 0 ? items.length - 1 : prevIndex - 1
      );
    };

    const downHandler = () => {
      setSelectedIndex((prevIndex) => 
        prevIndex === items.length - 1 ? 0 : prevIndex + 1
      );
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler();
          return true;
        }

        if (event.key === 'ArrowDown') {
          downHandler();
          return true;
        }

        if (event.key === 'Enter') {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-2 min-w-[280px] max-h-[300px] overflow-y-auto z-50">
        {items.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
            No results found
          </div>
        ) : (
          items.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors ${
                  index === selectedIndex
                    ? 'bg-primary/10 text-primary dark:bg-primary/15'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
                onClick={() => selectItem(index)}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{item.title}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {item.description}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    );
  }
);

SlashCommandsList.displayName = 'SlashCommandsList';

const getDefaultCommands = (
  onImageUpload?: (callback: (imageUrl: string) => void) => void,
  onVideoUpload?: (callback: (videoUrl: string) => void) => void
): SlashCommand[] => [
  {
    title: 'Text',
    description: 'Just start typing with plain text',
    icon: Type,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
    },
  },
  {
    title: 'Heading 1',
    description: 'Big section heading',
    icon: Heading1,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleHeading({ level: 1 }).run();
    },
    aliases: ['h1', 'heading1'],
  },
  {
    title: 'Heading 2',
    description: 'Medium section heading',
    icon: Heading2,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleHeading({ level: 2 }).run();
    },
    aliases: ['h2', 'heading2'],
  },
  {
    title: 'Heading 3',
    description: 'Small section heading',
    icon: Heading3,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleHeading({ level: 3 }).run();
    },
    aliases: ['h3', 'heading3'],
  },
  {
    title: 'Bullet List',
    description: 'Create a simple bullet list',
    icon: List,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
    aliases: ['ul', 'bulletlist'],
  },
  {
    title: 'Numbered List',
    description: 'Create a list with numbering',
    icon: ListOrdered,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
    aliases: ['ol', 'orderedlist', 'numbered'],
  },
  {
    title: 'Image',
    description: 'Upload or embed with a link',
    icon: Image,
    command: ({ editor, range }) => {
      if (onImageUpload) {
        // First delete the slash command range
        editor.chain().focus().deleteRange(range).run();
        
        onImageUpload((imageUrl: string) => {
          // Insert image followed by an empty paragraph
          editor.chain().focus().setImage({ src: imageUrl }).run();
          
          // Add an empty paragraph after the image
          setTimeout(() => {
            if (editor) {
              editor.chain().focus().createParagraphNear().run();
              // Position cursor in the new empty paragraph
              const pos = editor.state.doc.content.size;
              editor.commands.setTextSelection(pos - 1);
            }
          }, 50);
        });
      } else {
        // Fallback to old method if no upload handler is provided
        const url = window.prompt('Enter image URL:');
        if (url) {
          // Insert image followed by an empty paragraph
          editor.chain().focus().deleteRange(range).setImage({ src: url }).run();
          
          // Add an empty paragraph after the image
          setTimeout(() => {
            if (editor) {
              editor.chain().focus().createParagraphNear().run();
              // Position cursor in the new empty paragraph
              const pos = editor.state.doc.content.size;
              editor.commands.setTextSelection(pos - 1);
            }
          }, 50);
        } else {
          editor.chain().focus().deleteRange(range).run();
        }
      }
    },
    aliases: ['img', 'picture'],
  },
  {
    title: 'Video',
    description: 'Embed a video from YouTube or Vimeo',
    icon: Video,
    command: ({ editor, range }) => {
      if (onVideoUpload) {
        // First delete the slash command range
        editor.chain().focus().deleteRange(range).run();
        
        onVideoUpload((embedUrl: string) => {
          // Insert video container with an empty paragraph after it
          const videoWithEmptyLine = `<div class="video-embed-container" style="position: relative; width: 100%; max-width: 100%; aspect-ratio: 16 / 9; background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; margin: 16px 0; overflow: hidden;"><iframe src="${embedUrl}" style="width: 100%; height: 100%; border: 0; border-radius: 8px;" allowfullscreen="true" loading="lazy" title="Embedded video"></iframe></div><p></p>`;
          
          editor.chain().focus().insertContent(videoWithEmptyLine).run();
          
          // Add an empty paragraph after the video with proper cursor positioning
          setTimeout(() => {
            if (editor) {
              const { state } = editor;
              const { doc } = state;
              
              // Find the empty paragraph after the video and position cursor there
              let targetPos = -1;
              doc.descendants((node: any, pos: number) => {
                if (node.type.name === 'paragraph' && node.textContent === '') {
                  const $pos = doc.resolve(pos);
                  const prevNode = $pos.nodeBefore;
                  
                  // Check if previous node is a paragraph containing a video embed
                  if (prevNode && prevNode.type.name === 'paragraph') {
                    const htmlContent = prevNode.textContent;
                    if (htmlContent.includes('video-embed-container')) {
                      targetPos = pos + 1; // Position inside the empty paragraph
                    }
                  }
                }
              });
              
              if (targetPos > -1) {
                editor.commands.setTextSelection(targetPos);
              }
            }
          }, 50);
        });
      } else {
        // Fallback to old method if no upload handler is provided
        const url = window.prompt('Enter video URL:');
        if (url) {
          const getEmbedUrl = (inputUrl: string): string => {
            // Convert YouTube URLs
            const youtubeMatch = inputUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
            if (youtubeMatch) {
              return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
            }
            
            // Convert Vimeo URLs
            const vimeoMatch = inputUrl.match(/vimeo\.com\/(\d+)/);
            if (vimeoMatch) {
              return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
            }
            
            // If it's already an embed URL or other format, use as is
            return inputUrl;
          };

          const embedUrl = getEmbedUrl(url);
          
          // Delete the slash command text first
          editor.chain().focus().deleteRange(range).run();
          
          // Insert video container with an empty paragraph after it
          const videoWithEmptyLine = `<div class="video-embed-container" style="position: relative; width: 100%; max-width: 100%; aspect-ratio: 16 / 9; background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; margin: 16px 0; overflow: hidden;"><iframe src="${embedUrl}" style="width: 100%; height: 100%; border: 0; border-radius: 8px;" allowfullscreen="true" loading="lazy" title="Embedded video"></iframe></div><p></p>`;
          
          editor.chain().focus().insertContent(videoWithEmptyLine).run();
          
          // Add an empty paragraph after the video with proper cursor positioning
          setTimeout(() => {
            if (editor) {
              const { state } = editor;
              const { doc } = state;
              
              // Find the empty paragraph after the video and position cursor there
              let targetPos = -1;
              doc.descendants((node: any, pos: number) => {
                if (node.type.name === 'paragraph' && node.textContent === '') {
                  const $pos = doc.resolve(pos);
                  const prevNode = $pos.nodeBefore;
                  
                  // Check if previous node is a paragraph containing a video embed
                  if (prevNode && prevNode.type.name === 'paragraph') {
                    const htmlContent = prevNode.textContent;
                    if (htmlContent.includes('video-embed-container')) {
                      targetPos = pos + 1; // Position inside the empty paragraph
                    }
                  }
                }
              });
              
              if (targetPos > -1) {
                editor.commands.setTextSelection(targetPos);
              }
            }
          }, 50);
        } else {
          editor.chain().focus().deleteRange(range).run();
        }
      }
    },
    aliases: ['embed'],
  },
  {
    title: 'Link',
    description: 'Add a link',
    icon: Link,
    command: ({ editor, range }) => {
      const url = window.prompt('Enter URL:');
      if (url) {
        const text = window.prompt('Enter link text:') || url;
        editor.chain().focus().deleteRange(range).insertContent(`<a href="${url}">${text}</a>`).run();
      } else {
        editor.chain().focus().deleteRange(range).run();
      }
    },
    aliases: ['url'],
  },
  {
    title: 'Quote',
    description: 'Capture a quote',
    icon: Quote,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
    aliases: ['blockquote'],
  },
  {
    title: 'Code Block',
    description: 'Capture a code snippet',
    icon: Code,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
    aliases: ['codeblock', 'code'],
  },
  {
    title: 'Divider',
    description: 'Visually divide blocks',
    icon: Minus,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).setHorizontalRule().run();
    },
    aliases: ['hr', 'horizontalrule', 'divider'],
  },
];

export const SlashCommands = Extension.create<SlashCommandsOptions>({
  name: 'slashCommands',

  addOptions() {
    return {
      commands: undefined,
      onImageUpload: undefined,
      onVideoUpload: undefined,
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        char: '/',
        pluginKey: new PluginKey('slashCommands'),
        command: ({ editor, range, props }: { editor: any; range: any; props: any }) => {
          props.command({ editor, range });
        },
        items: ({ query }: { query: string }) => {
          const commands = this.options.commands || getDefaultCommands(this.options.onImageUpload, this.options.onVideoUpload);
          return commands.filter(command => {
            const searchTerm = query.toLowerCase();
            return (
              command.title.toLowerCase().includes(searchTerm) ||
              command.description.toLowerCase().includes(searchTerm) ||
              command.aliases?.some(alias => alias.toLowerCase().includes(searchTerm))
            );
          });
        },
        render: () => {
          let component: ReactRenderer;
          let popup: any;

          return {
            onStart: (props: any) => {
              component = new ReactRenderer(SlashCommandsList as any, {
                props,
                editor: props.editor,
              });

              if (!props.clientRect) {
                return;
              }

              popup = tippy('body', {
                getReferenceClientRect: props.clientRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                zIndex: 40, // Lower than modal z-index
              });
            },

            onUpdate(props: any) {
              component.updateProps(props);

              if (!props.clientRect) {
                return;
              }

              popup[0].setProps({
                getReferenceClientRect: props.clientRect,
              });
            },

            onKeyDown(props: any) {
              if (props.event.key === 'Escape') {
                popup[0].hide();
                return true;
              }

              return (component.ref as SlashCommandsListRef)?.onKeyDown(props) || false;
            },

            onExit() {
              popup[0].destroy();
              component.destroy();
            },
          };
        },
      } as SuggestionOptions),
    ];
  },
}); 